import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { AuthService } from './auth.service';

const IDLE_TICK_MS = 1_000;
const STORAGE_DB_LOCATION = 'isaMed:dbLocation';
const STORAGE_CHARGE_BATCH = 'isaMed:chargeBatchAdminId';
const STORAGE_PAYMENT_BATCH = 'isaMed:paymentBatchAdminId';

const ACTIVITY_EVENTS: Array<keyof DocumentEventMap> = [
  'mousedown',
  'mousemove',
  'keydown',
  'touchstart',
  'scroll',
  'click'
];

@Injectable({
  providedIn: 'root'
})
export class AppStatusBarService implements OnDestroy {
  private readonly chargeBatchSubject = new BehaviorSubject<string>('');
  private readonly paymentBatchSubject = new BehaviorSubject<string>('');
  private readonly dbLocationSubject = new BehaviorSubject<string>('');
  private readonly idleSecondsSubject = new BehaviorSubject<number>(0);
  private readonly userIdleTimeLimitSubject = new BehaviorSubject<number>(0);

  readonly chargeBatch$ = this.chargeBatchSubject.asObservable();
  readonly paymentBatch$ = this.paymentBatchSubject.asObservable();
  readonly dbLocation$ = this.dbLocationSubject.asObservable();
  readonly idleSeconds$ = this.idleSecondsSubject.asObservable();
  readonly userIdleTimeLimit$ = this.userIdleTimeLimitSubject.asObservable();

  private lastActivityAt = Date.now();
  private idleTimer: ReturnType<typeof setInterval> | null = null;
  private idleLogoutTriggered = false;
  private activityHandler = (): void => this.markActivity();

  constructor(
    private ngZone: NgZone,
    private authService: AuthService,
    private router: Router
  ) {
    this.refreshFromStorage();
    if (typeof window !== 'undefined') {
      this.ngZone.runOutsideAngular(() => {
        for (const eventName of ACTIVITY_EVENTS) {
          document.addEventListener(eventName, this.activityHandler, { passive: true });
        }
        this.idleTimer = setInterval(() => this.updateIdleSeconds(), IDLE_TICK_MS);
      });
      this.updateIdleSeconds();
    }
  }

  ngOnDestroy(): void {
    if (typeof window === 'undefined') {
      return;
    }
    for (const eventName of ACTIVITY_EVENTS) {
      document.removeEventListener(eventName, this.activityHandler);
    }
    if (this.idleTimer != null) {
      clearInterval(this.idleTimer);
      this.idleTimer = null;
    }
  }

  get chargeBatch(): string {
    return this.chargeBatchSubject.value;
  }

  get paymentBatch(): string {
    return this.paymentBatchSubject.value;
  }

  get dbLocation(): string {
    return this.dbLocationSubject.value;
  }

  get idleSeconds(): number {
    return this.idleSecondsSubject.value;
  }

  get userIdleTimeLimit(): number {
    return this.userIdleTimeLimitSubject.value;
  }

  /** Delphi VirtualSecurity.SetBatchAdminId — session + status bar; only when id > 0. */
  setChargeBatch(batch: string | number | null | undefined): void {
    const normalized = this.normalizePositiveBatch(batch);
    if (!normalized) {
      return;
    }
    this.publishChargeBatch(normalized);
    this.persistString(STORAGE_CHARGE_BATCH, normalized);
  }

  /** Delphi VirtualSecurity.SetPaymentBatchAdminId */
  setPaymentBatch(batch: string | number | null | undefined): void {
    const normalized = this.normalizePositiveBatch(batch);
    if (!normalized) {
      return;
    }
    this.publishPaymentBatch(normalized);
    this.persistString(STORAGE_PAYMENT_BATCH, normalized);
  }

  /** Delphi LoginFM.fsHostName from edtDBLocation. */
  setDbLocation(location: string | null | undefined): void {
    const value = String(location ?? '').trim();
    this.ngZone.run(() => this.dbLocationSubject.next(value));
    if (value) {
      this.persistString(STORAGE_DB_LOCATION, value);
    }
  }

  /** Delphi VirtualSecurity.UserIdelTime (seconds). */
  setUserIdleTimeLimit(seconds: number | null | undefined): void {
    const limit = Number(seconds);
    this.ngZone.run(() => {
      this.userIdleTimeLimitSubject.next(Number.isFinite(limit) && limit > 0 ? Math.floor(limit) : 0);
    });
  }

  initializeForAuthenticatedUser(dbLocation?: string | null): void {
    if (dbLocation) {
      this.setDbLocation(dbLocation);
    } else {
      const user = this.authService.getCurrentUser();
      if (user?.dbLocation) {
        this.setDbLocation(user.dbLocation);
      }
    }
    this.refreshFromStorage();
    this.idleLogoutTriggered = false;
    this.markActivity();
  }

  /** Re-read persisted Delphi registry equivalents (batch ids, location). */
  refreshFromStorage(): void {
    const charge = this.readPersistedString(STORAGE_CHARGE_BATCH);
    const payment = this.readPersistedString(STORAGE_PAYMENT_BATCH);
    const location = this.readPersistedString(STORAGE_DB_LOCATION);
    if (charge) {
      this.publishChargeBatch(charge);
    }
    if (payment) {
      this.publishPaymentBatch(payment);
    }
    if (location) {
      this.ngZone.run(() => this.dbLocationSubject.next(location));
    }
  }

  private publishChargeBatch(value: string): void {
    if (this.chargeBatchSubject.value === value) {
      return;
    }
    this.ngZone.run(() => this.chargeBatchSubject.next(value));
  }

  private publishPaymentBatch(value: string): void {
    if (this.paymentBatchSubject.value === value) {
      return;
    }
    this.ngZone.run(() => this.paymentBatchSubject.next(value));
  }

  private markActivity(): void {
    this.lastActivityAt = Date.now();
    if (this.idleSecondsSubject.value !== 0) {
      this.ngZone.run(() => this.idleSecondsSubject.next(0));
    }
    this.idleLogoutTriggered = false;
  }

  private updateIdleSeconds(): void {
    const seconds = Math.max(0, Math.floor((Date.now() - this.lastActivityAt) / 1_000));
    if (seconds !== this.idleSecondsSubject.value) {
      this.ngZone.run(() => this.idleSecondsSubject.next(seconds));
    }
    this.maybeLogoutForIdle(seconds);
  }

  private maybeLogoutForIdle(idleSeconds: number): void {
    const limit = this.userIdleTimeLimitSubject.value;
    if (
      limit <= 0 ||
      idleSeconds <= limit ||
      this.idleLogoutTriggered ||
      !this.authService.isAuthenticated()
    ) {
      return;
    }
    this.idleLogoutTriggered = true;
    this.ngZone.run(() => {
      this.authService.logout().subscribe({
        next: () => undefined,
        error: () => this.router.navigate(['/login'])
      });
    });
  }

  private normalizePositiveBatch(batch: string | number | null | undefined): string {
    if (batch == null || batch === '') {
      return '';
    }
    const num = Number(batch);
    if (Number.isFinite(num) && num > 0) {
      return String(Math.floor(num));
    }
    const text = String(batch).trim();
    const parsed = Number(text);
    if (Number.isFinite(parsed) && parsed > 0) {
      return String(Math.floor(parsed));
    }
    return '';
  }

  private persistString(key: string, value: string): void {
    try {
      localStorage.setItem(key, value);
    } catch {
      // ignore storage failures
    }
  }

  private readPersistedString(key: string): string {
    try {
      return String(localStorage.getItem(key) ?? '').trim();
    } catch {
      return '';
    }
  }
}
